import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { CalculationModelService } from '../../../services/calculation-model-service';
import { CalculationModelBase } from '../calculation-model-base';
import { process } from "@progress/kendo-data-query";
import { GridComponent } from '@progress/kendo-angular-grid';
import { CalculationModelTemplate } from '../../../models/calculation-model-template';
import { GenericPolluxResult } from '../../../../../models/generic-pollux-result';
import { UploadComponent } from '@progress/kendo-angular-upload';
import { ImportPreviewRenderService } from '../../../../../components/common/services/import-preview-render-service';
import { AppServiceHelperService } from '../../../../../services/app-service-helper.service';
import { FileDownloadService } from '../../../../../services/file-download-service.service';
import { EventBusService } from '../../../../../services/event-bus.service';
import { AppLoadingIndicatorService } from '../../../../../services/app-loading-indicator.service';

/**
 * Handles calculation model editing component
 */
@Component({
  selector: 'calculation-model-edit',
  templateUrl: './calculation-model-edit.component.html',
  styleUrls: ['./../calculation-model-register/calculation-model-register.component.scss'],//!! we are using css from register
  encapsulation: ViewEncapsulation.None
})
export class CalculationModelEditComponent extends CalculationModelBase implements OnInit, OnDestroy {
  /**
   * Close pane event
   */
  @Output() closePane: EventEmitter<any> = new EventEmitter();

  /**
   * Close pane event with refresh
   */
  @Output() closeWithRefresh: EventEmitter<any> = new EventEmitter();

  /**
 * Number of steps in the process
 */
  stepsCount = 3;

  /**
 * Current step in the process
 */
  currentStep = 1;

  /**
   * Flag indicating if upload has been requested
   */
  uploadRequested = false;

  /**
   * Session GUID for the process
   */
  private sessionGuid: string;

  /**
 * The Kendo upload component for uploading the file
 */
  @ViewChild("kendoUpload") kendoUpload: UploadComponent;

  /**
  * The Kendo grid component for template editing
  */
  @ViewChild("templateEditGrid") templateEditGrid: GridComponent;

  /**
   * Constructor
   * @param cmService
   * @param ais
   * @param elRef
   * @param translate
   * @param iprs
   */
  constructor(cmService: CalculationModelService,
    eventBus: EventBusService,
    elRef: ElementRef,
    private appServiceHelper: AppServiceHelperService,
    public iprs: ImportPreviewRenderService,
    private fileDownloadService: FileDownloadService,
    protected loaderService: AppLoadingIndicatorService) {
    super(cmService, eventBus, elRef, loaderService);

    this.stepsCount = 3;
  }

  /**
   * Lifecycle hook called after the component is initialized
   */
  ngOnInit(): void {
    this.subscriptions.add(this.cmService.selectedId$.subscribe(id => {
      if (id > 0) {
        this.cmService.startEditSession().subscribe((guid: string) => {
          this.sessionGuid = guid;
        });

        this.loadReflists().then(() => {
          this.cmService.getCalculationModel(id).subscribe((model: CalculationModelTemplate) => {
            this.templateDto = model;

            this.uploadedFiles = [{
              ...model,
              fileName: model.originalFilename,
              isSelected: true,
              groupId: this.templateImportGroup,
              groupDescription: this.importGroups.find(g => g.type === this.templateImportGroup).value,
              mappingVersionDescription: this.mappingVersions.find(m => m.oid === model.mappingVersionId).description,
              canUpgrade: model.mappingVersionId !== this.newestTemplateMappingVersion.oid
            }];

            this.templatesList.push(this.templateDto);
            this.gridData = process(this.templatesList, this.gridState);
            //set grid (for template info) in edit mode
            setTimeout(() => {
              this.templateEditGrid.editRow(0);
            }, 100);
          });
        });
      }
    }));
  }

  /**
   * Lifecycle hook called before the component is destroyed
   */
  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.cmService.endEditSession(this.sessionGuid).subscribe((grp: GenericPolluxResult) => {
      this.sessionGuid = null;//fire and forget
    });
  }

  /**
   * Event handler for the close button click
   */
  onCloseClicked(): void {
    this.shouldClose = true;
    setTimeout(() => {
      this.closePane.emit();
    }, 500);//let animation finish
  }

  /**
   * Go to the previous step
   */
  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep -= 1;
    }
  }

  /**
   * Check if the next step is enabled
   */
  isNextEnabled(): boolean {
    if (this.currentStep === 1) {
      return !this.uploadRequested;
    }
    return false;
  }

  /**
   * Go to the next step
   */
  async nextStep(): Promise<void> {
    if (this.currentStep >= this.stepsCount) {
      return;
    }

    if (this.currentStep === 1) {
      this.currentStep = 2;
    }
  }

  /**
   * Show error message
   * @param message The error message
   * @param resolve The optional resolve function
   */
  private showError(message: string, resolve: (boolean) => void = null) {
    this.showLoader(false);
    alert(message);
    if (resolve) {
      resolve(false);
    }
  }

  /**
   * Upgrade the mapping version of a calculation model template
   * @param item The calculation model template
   * @returns A promise that resolves to a boolean indicating the success of the upgrade
   */
  async upgradeMappingVersion(item: CalculationModelTemplate): Promise<boolean> {
    this.showLoader(true);
    const errorMessage = 'Failed to upgrade mapping version!';
    return new Promise((resolve, _reject) => {
      this.cmService.upgradeMappingVersion(this.sessionGuid, item.oid).subscribe({
        next: (gpr: GenericPolluxResult) => {
          this.showLoader(false);
          if (gpr.success) {
            // All went ok, adjust models
            const model = this.templateDto;
            model.mappingVersionId = this.newestTemplateMappingVersion.oid;

            const fileModel = this.uploadedFiles[0];
            fileModel.mappingVersionId = this.newestTemplateMappingVersion.oid;
            fileModel.mappingVersionDescription = this.mappingVersions.find(m => m.oid === model.mappingVersionId).description;
            fileModel.canUpgrade = false;

            this.previewData = null;

            resolve(true);
          } else {
            this.showError(errorMessage, resolve);
          }
        },
        error: (_reason) => {
          this.showError(errorMessage, resolve);
        }
      });
    });
  }

  /**
   * Trigger the upload of the template
   * @param item The calculation model template
   */
  uploadEditTemplate(_item: CalculationModelTemplate): void {
    this.uploadRequested = true;
  }

  /**
   * Remove a file from the upload
   * @param upload The upload component
   * @param uid The unique identifier of the file to remove
   */
  onRemoveFile(upload: UploadComponent, uid: string): void {
    upload.removeFilesByUid(uid);
    this.importFiles = [];
    this.previewData = null;
  }

  /**
   * Confirm the upload of the template
   * @returns A promise that resolves to a boolean indicating the success of the upload
   */
  async uploadConfirmed(): Promise<boolean> {
    if (this.importFiles.length === 0) return false; // Nothing selected

    this.showLoader(true);
    const errorMessage = 'Failed to upload file!';
    return new Promise((resolve, _reject) => {
      const dataUrl = this.importFiles[0].dataUrl;
      this.cmService.uploadSessionTemplate(this.sessionGuid, this.templateDto.oid, dataUrl).subscribe({
        next: (gpr: GenericPolluxResult) => {
          this.showLoader(false);
          if (gpr.success) {
            // All went ok
            this.uploadRequested = false;
            this.previewData = null;

            resolve(true);
          } else {
            this.showError(errorMessage, resolve);
          }
        },
        error: (_reason) => {
          this.showError(errorMessage, resolve);
        }
      });
    });
  }

  /**
   * Cancel the upload
   */
  uploadCanceled(): void {
    this.kendoUpload.fileList.clear();
    this.importFiles = [];
    this.uploadRequested = false;
  }

  /**
   * Ensure the working file is available
   * @returns A promise that resolves to a boolean indicating the success of ensuring the working file
   */
  async ensureWorkingFile(): Promise<boolean> {
    return new Promise((resolve, _reject) => {
      this.cmService.ensureWorkingFile(this.sessionGuid, this.templateDto.oid).subscribe({
        next: (gpr: GenericPolluxResult) => {
          resolve(gpr.success);
        },
        error: (_reason) => {
          resolve(false);
        }
      });
    });
  }

  /**
   * Upload the template
   * @returns A promise that resolves to a boolean indicating the success of the upload
   */
  async uploadTemplate(): Promise<boolean> {
    const request = {
      isTemplate: true,
      workingTemplateGuid: this.sessionGuid,
      importFiles: this.uploadedFiles // We already have
    };

    return new Promise((resolve, _reject) => {
      this.cmService.uploadCalculationModelTemplate(request).subscribe({
        next: (result: any) => {
          const uploadedFile = result.data[0];

          const fileModel = this.uploadedFiles[0];
          fileModel.importGuid = uploadedFile.importGuid;
          fileModel.fileImportGuid = uploadedFile.fileImportGuid;

          resolve(true);
        },
        error: (_reason) => {
          alert('Failed to upload template!');
          resolve(false);
        }
      });
    });
  }

  /**
   * Prepare and start the pre-uploaded import preview
   * @returns A promise that resolves to a boolean indicating the success of the operation
   */
  async prepareAndStartPreuploadedImportPreview(): Promise<boolean> {
    this.showLoader(true);
    return new Promise((resolve, _reject) => {
      this.ensureWorkingFile().then((fileCreated) => {
        if (fileCreated) {
          this.uploadTemplate().then((templateUploaded) => {
            if (templateUploaded) {
              this.startPreuploadedImportPreview().then((previewResult) => {
                if (previewResult) {
                  this.showLoader(false);
                  resolve(true); // Will continue to the next step
                }
                else {
                  // Error if we got here
                  this.showLoader(false);
                  alert('Failed to generate preview!');
                  resolve(false);
                }
              });
            }
            else {
              // Error if we got here
              this.showLoader(false);
              alert('Failed to generate preview!');
              resolve(false);
            }
          });
        }

        else {
          // Error if we got here
          this.showLoader(false);
          alert('Failed to generate preview!');
          resolve(false);
        }
      });
    });
  }

  /**
   * Save the template
   */
  saveTemplate(): void {
    const calcModel = this.gridData.data[0] as CalculationModelTemplate;
    if (!calcModel.name) {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          // Nothing
        }
      }, 'templateName');
      return;
    }

    this.showLoader(true);
    this.cmService.updateCalculationModel(this.sessionGuid, calcModel).subscribe({
      next: (saved: any) => {
        this.showLoader(false);
        if (saved && saved.oid > 0) {
          // All went ok, close pane
          this.shouldClose = true;
          setTimeout(() => {
            this.closeWithRefresh.emit();
          }, 500); // Let animation finish
        } else {
          alert('Failed to save template!');
        }
      },
      error: (_reason) => {
        this.showLoader(false);
        alert('Failed to save template!');
      }
    });
  }

  /**
   * Download the file associated with the calculation model template
   * @param item The calculation model template
   */
  downloadFileItem(item: CalculationModelTemplate): void {
    const url = `calculationmodeltemplate/downloadfile/${this.sessionGuid}/${item.oid}`;
    const downloadUrl = this.appServiceHelper.getServiceEndpoint('pollux.settings.api', url);
    this.fileDownloadService.initiateDownload(downloadUrl, null, false);
  }
}
